import { useCallback, useEffect, useState } from 'react'
import { Pause, Play, Plus, RefreshCw, Repeat, XCircle } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { financeSummaryService, type RecurringTemplate, type RecurringStatus } from '@/services/financeSummaryService'
import { RecurringTemplateForm } from '@/components/finance-v2/RecurringTemplateForm'
import { AmountText, FinanceStatusBadge, toneForTransactionType } from '@/components/finance-v2/FinanceBadges'
import { formatBRL, formatIsoDate } from '@/lib/finance'
import { getErrorMessage } from '@/utils/error.utils'

const STATUS_LABELS: Record<RecurringStatus, string> = {
  ACTIVE: 'Ativo',
  PAUSED: 'Pausado',
  FINISHED: 'Concluído',
  CANCELLED: 'Cancelado',
}

export function valueLabel(t: Pick<RecurringTemplate, 'totalAmount' | 'installmentAmount' | 'installments'>): string {
  if (t.totalAmount !== null && t.installments) return `${formatBRL(t.totalAmount)} em ${t.installments}x`
  return `${formatBRL(t.installmentAmount ?? 0)} por parcela${t.installments ? ` (${t.installments}x)` : ' (sem fim)'}`
}

/**
 * Modelos recorrentes: o job diário gera as parcelas que vencem até o fim do
 * mês corrente (anuidades novas usam um modelo; as antigas continuam como estão).
 */
export default function FinanceV2RecurringPage() {
  const [templates, setTemplates] = useState<RecurringTemplate[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [showForm, setShowForm] = useState(false)

  const load = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      setTemplates(await financeSummaryService.recurring.list())
    } catch (err) {
      setError(getErrorMessage(err, 'Erro ao carregar os modelos recorrentes'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const act = async (id: string, action: 'pause' | 'resume' | 'cancel') => {
    if (action === 'cancel' && !window.confirm('Cancelar o modelo? As parcelas já geradas continuam como estão.')) return
    try {
      await financeSummaryService.recurring.action(id, action)
      load()
    } catch (err) {
      setError(getErrorMessage(err, 'Erro ao alterar o modelo'))
    }
  }

  const runNow = async () => {
    try {
      const r = await financeSummaryService.recurring.run()
      setNotice(`${r.generated} parcela(s) gerada(s) em ${r.templates} modelo(s) ativo(s).`)
      load()
    } catch (err) {
      setError(getErrorMessage(err, 'Erro ao gerar as parcelas'))
    }
  }

  return (
    <div className="flex flex-col gap-4 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex items-center gap-2">
            <Repeat className="size-6 text-muted-foreground" />
            <h1 className="text-2xl font-semibold">Recorrências</h1>
          </div>
          <p className="text-sm text-muted-foreground">
            Mensalidades e parcelas geradas mês a mês (todo dia, as que vencem até o fim do mês).
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={runNow}><RefreshCw className="size-4" />Gerar parcelas do mês</Button>
          <Button onClick={() => setShowForm(true)}><Plus className="size-4" />Novo modelo</Button>
        </div>
      </div>

      {error && (
        <p role="alert" className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">{error}</p>
      )}
      {notice && <p role="status" className="rounded-lg border border-border bg-muted/40 p-3 text-sm">{notice}</p>}

      {showForm && (
        <RecurringTemplateForm
          onCancel={() => setShowForm(false)}
          onCreated={(t) => {
            setShowForm(false)
            setNotice(`Modelo "${t.description}" criado; ${t.generatedCount} parcela(s) já gerada(s).`)
            load()
          }}
        />
      )}

      {loading ? (
        <div className="flex flex-col gap-2" aria-busy="true">
          <span className="sr-only">Carregando…</span>
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-12" />)}
        </div>
      ) : templates.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-6 text-center">
            <Repeat className="size-10 text-muted-foreground" aria-hidden="true" />
            <p className="text-sm text-muted-foreground">Nenhum modelo recorrente.</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="min-w-0 py-0">
          <div className="min-w-0 p-2">
            <Table className="min-w-[760px]">
              <TableHeader>
                <TableRow>
                  <TableHead>Descrição</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Valor</TableHead>
                  <TableHead>1º vencimento</TableHead>
                  <TableHead>Geradas</TableHead>
                  <TableHead>Situação</TableHead>
                  <TableHead>Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {templates.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell className="font-medium">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="break-words">{t.description}</span>
                        {t.annualFeeId && <Badge variant="secondary">anuidade</Badge>}
                      </div>
                    </TableCell>
                    <TableCell>{t.type === 'INCOME' ? 'A receber' : 'A pagar'}</TableCell>
                    <TableCell><AmountText tone={toneForTransactionType(t.type)}>{valueLabel(t)}</AmountText></TableCell>
                    <TableCell className="tabular-nums">{formatIsoDate(t.firstDueDate)}</TableCell>
                    <TableCell className="tabular-nums">{t.generatedCount}{t.installments ? `/${t.installments}` : ''}</TableCell>
                    <TableCell><FinanceStatusBadge status={t.status}>{STATUS_LABELS[t.status]}</FinanceStatusBadge></TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {t.status === 'ACTIVE' && (
                          <Button type="button" variant="ghost" size="icon" aria-label="Pausar" className="text-muted-foreground" onClick={() => act(t.id, 'pause')}>
                            <Pause className="size-4" />
                          </Button>
                        )}
                        {t.status === 'PAUSED' && (
                          <Button type="button" variant="ghost" size="icon" aria-label="Retomar" className="text-primary" onClick={() => act(t.id, 'resume')}>
                            <Play className="size-4" />
                          </Button>
                        )}
                        {(t.status === 'ACTIVE' || t.status === 'PAUSED') && !t.annualFeeId && (
                          <Button type="button" variant="ghost" size="icon" aria-label="Cancelar modelo" className="text-muted-foreground hover:text-destructive" onClick={() => act(t.id, 'cancel')}>
                            <XCircle className="size-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
    </div>
  )
}
